import "./styles.css";
import { loadQuestionChunks } from "./content/questions";
import {
  QUESTION_BANK_VERSION,
  PROGRESS_SCHEMA_VERSION,
  ResourceBudget,
} from "./domain/config";
import { AppError, ErrorClassification, classifyUnknownError } from "./domain/errors";
import { summarizeProgress, weakestDomains } from "./domain/progress";
import {
  domainsForMode,
  questionCountForMode,
  scoreAttempt,
  selectQuestions,
} from "./domain/scoring";
import {
  ExamMode,
  ProgressEventType,
  ViewState,
  type AttemptCompletedEvent,
  type ProgressEvent,
  type ProgressSummary,
  type Question,
  type ResponseMap,
  type ScoreReport,
} from "./domain/types";
import { LocalProgressStore } from "./infrastructure/progress-store";
import { createTraceId } from "./infrastructure/trace";
import {
  SubmissionState,
  renderApp,
  type ResultsRenderModel,
} from "./ui/render";

const FOCUS_DOMAIN_COUNT = 3;
const RANDOM_SEED_ARRAY_LENGTH = 1;

interface ExamSession {
  readonly attemptId: string;
  readonly traceId: string;
  readonly mode: ExamMode;
  readonly questions: readonly Question[];
  readonly cursorIndex: number;
  readonly responses: ResponseMap;
  readonly submissionState: (typeof SubmissionState)[keyof typeof SubmissionState];
}

type AppState =
  | { readonly view: typeof ViewState.DASHBOARD }
  | { readonly view: typeof ViewState.STUDY }
  | { readonly view: typeof ViewState.LABS }
  | { readonly view: typeof ViewState.LOADING; readonly mode: ExamMode }
  | { readonly view: typeof ViewState.EXAM; readonly session: ExamSession }
  | { readonly view: typeof ViewState.RESULTS; readonly results: ResultsRenderModel }
  | { readonly view: typeof ViewState.ERROR; readonly error: AppError };

class AppController {
  readonly #root: HTMLElement;
  readonly #store: LocalProgressStore;
  #state: AppState = { view: ViewState.DASHBOARD };
  #events: readonly ProgressEvent[] = [];

  constructor(root: HTMLElement, store: LocalProgressStore) {
    this.#root = root;
    this.#store = store;
    this.#root.addEventListener("click", (event) => this.#handleClick(event));
    this.#root.addEventListener("change", (event) => this.#handleChange(event));
  }

  initialize(): void {
    try {
      this.#events = this.#store.load();
      this.#transition({ view: ViewState.DASHBOARD });
    } catch (error: unknown) {
      this.#transition({ view: ViewState.ERROR, error: classifyUnknownError(error) });
    }
  }

  #summary(): ProgressSummary {
    return summarizeProgress(this.#events);
  }

  #transition(state: AppState): void {
    this.#state = state;
    const summary = this.#summary();

    switch (state.view) {
      case ViewState.DASHBOARD:
        renderApp(this.#root, { view: state.view, summary });
        break;
      case ViewState.STUDY:
        renderApp(this.#root, { view: state.view, summary });
        break;
      case ViewState.LABS:
        renderApp(this.#root, { view: state.view, summary });
        break;
      case ViewState.LOADING:
        renderApp(this.#root, { view: state.view, summary, mode: state.mode });
        break;
      case ViewState.EXAM:
        renderApp(this.#root, { view: state.view, summary, exam: state.session });
        break;
      case ViewState.RESULTS:
        renderApp(this.#root, { view: state.view, summary, results: state.results });
        break;
      case ViewState.ERROR:
        renderApp(this.#root, { view: state.view, summary, error: state.error });
        break;
    }

    document.querySelector<HTMLElement>("#main-content")?.focus({ preventScroll: true });
  }

  #handleClick(event: Event): void {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const actionElement = target.closest<HTMLElement>("[data-action]");
    if (actionElement === null) {
      return;
    }

    try {
      const action = actionElement.dataset.action;
      switch (action) {
        case "NAV_DASHBOARD":
          this.#transition({ view: ViewState.DASHBOARD });
          break;
        case "NAV_STUDY":
          this.#transition({ view: ViewState.STUDY });
          break;
        case "NAV_LABS":
          this.#transition({ view: ViewState.LABS });
          break;
        case "START_EXAM": {
          const mode = parseExamMode(actionElement.dataset.mode);
          void this.#startExam(mode);
          break;
        }
        case "PREVIOUS_QUESTION":
          this.#moveQuestion(-1);
          break;
        case "NEXT_QUESTION":
          this.#moveQuestion(1);
          break;
        case "GO_TO_QUESTION":
          this.#goToQuestion(parseQuestionIndex(actionElement.dataset.index));
          break;
        case "SUBMIT_EXAM":
          this.#requestSubmission();
          break;
        case "CONFIRM_SUBMIT":
          this.#completeAttempt();
          break;
        case undefined:
          break;
        default:
          throw new AppError(
            "The interface emitted an unknown action.",
            ErrorClassification.INVARIANT_VIOLATION,
            "UNKNOWN_UI_ACTION",
          );
      }
    } catch (error: unknown) {
      this.#transition({
        view: ViewState.ERROR,
        error: classifyUnknownError(error),
      });
    }
  }

  #handleChange(event: Event): void {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.name !== "answer") {
      return;
    }

    if (this.#state.view !== ViewState.EXAM) {
      this.#transition({
        view: ViewState.ERROR,
        error: new AppError(
          "An answer was received outside an active assessment.",
          ErrorClassification.INVARIANT_VIOLATION,
          "ANSWER_OUTSIDE_EXAM",
        ),
      });
      return;
    }

    const questionId = target.dataset.questionId;
    if (questionId === undefined || target.value.length === 0) {
      this.#transition({
        view: ViewState.ERROR,
        error: new AppError(
          "The selected answer failed input validation.",
          ErrorClassification.PERMANENT,
          "INVALID_ANSWER_INPUT",
        ),
      });
      return;
    }

    const session = this.#state.session;
    const currentQuestion = session.questions[session.cursorIndex];
    if (currentQuestion?.id !== questionId) {
      this.#transition({
        view: ViewState.ERROR,
        error: new AppError(
          "The selected answer does not belong to the displayed question.",
          ErrorClassification.INVARIANT_VIOLATION,
          "ANSWER_QUESTION_MISMATCH",
        ),
      });
      return;
    }

    if (!currentQuestion.choices.some((choice) => choice.id === target.value)) {
      this.#transition({
        view: ViewState.ERROR,
        error: new AppError(
          "The selected choice does not exist on the displayed question.",
          ErrorClassification.PERMANENT,
          "INVALID_ANSWER_CHOICE",
        ),
      });
      return;
    }

    this.#transition({
      view: ViewState.EXAM,
      session: {
        ...session,
        responses: { ...session.responses, [questionId]: target.value },
        submissionState: SubmissionState.READY,
      },
    });
  }

  async #startExam(mode: ExamMode): Promise<void> {
    try {
      const focusDomains = weakestDomains(this.#summary(), FOCUS_DOMAIN_COUNT).map(
        (domain) => domain.domain,
      );
      const domains = domainsForMode(mode, focusDomains);
      const traceId = createTraceId();
      this.#transition({ view: ViewState.LOADING, mode });
      const loadedQuestions = await loadQuestionChunks(traceId, domains);
      const questions = selectQuestions(
        loadedQuestions,
        domains,
        questionCountForMode(mode),
        createRandomSeed(),
      );

      this.#transition({
        view: ViewState.EXAM,
        session: {
          attemptId: crypto.randomUUID(),
          traceId,
          mode,
          questions,
          cursorIndex: 0,
          responses: {},
          submissionState: SubmissionState.READY,
        },
      });
    } catch (error: unknown) {
      this.#transition({ view: ViewState.ERROR, error: classifyUnknownError(error) });
    }
  }

  #moveQuestion(direction: -1 | 1): void {
    if (this.#state.view !== ViewState.EXAM) {
      return;
    }

    const nextIndex = this.#state.session.cursorIndex + direction;
    this.#goToQuestion(nextIndex);
  }

  #goToQuestion(index: number): void {
    if (this.#state.view !== ViewState.EXAM) {
      return;
    }

    const session = this.#state.session;
    if (index < 0 || index >= session.questions.length) {
      return;
    }

    this.#transition({
      view: ViewState.EXAM,
      session: {
        ...session,
        cursorIndex: index,
        submissionState: SubmissionState.READY,
      },
    });
  }

  #requestSubmission(): void {
    if (this.#state.view !== ViewState.EXAM) {
      return;
    }

    const session = this.#state.session;
    const unansweredCount =
      session.questions.length - Object.keys(session.responses).length;
    if (unansweredCount > 0) {
      this.#transition({
        view: ViewState.EXAM,
        session: {
          ...session,
          submissionState: SubmissionState.UNANSWERED_WARNING,
        },
      });
      return;
    }

    this.#completeAttempt();
  }

  #completeAttempt(): void {
    if (this.#state.view !== ViewState.EXAM) {
      return;
    }

    try {
      const session = this.#state.session;
      const report = scoreAttempt(session.questions, session.responses);
      const event = createAttemptEvent(session, report);
      this.#events = this.#store.append(event);
      this.#transition({
        view: ViewState.RESULTS,
        results: {
          mode: session.mode,
          questions: session.questions,
          report,
        },
      });
    } catch (error: unknown) {
      this.#transition({ view: ViewState.ERROR, error: classifyUnknownError(error) });
    }
  }
}

function createAttemptEvent(
  session: ExamSession,
  report: ScoreReport,
): AttemptCompletedEvent {
  return {
    schemaVersion: PROGRESS_SCHEMA_VERSION,
    eventType: ProgressEventType.ATTEMPT_COMPLETED,
    eventId: crypto.randomUUID(),
    attemptId: session.attemptId,
    traceId: session.traceId,
    occurredAt: new Date().toISOString(),
    mode: session.mode,
    questionBankVersion: QUESTION_BANK_VERSION,
    report,
  };
}

function parseExamMode(value: string | undefined): ExamMode {
  if (value !== undefined && Object.values(ExamMode).includes(value as ExamMode)) {
    return value as ExamMode;
  }

  throw new AppError(
    "Assessment mode failed input validation.",
    ErrorClassification.PERMANENT,
    "INVALID_EXAM_MODE",
  );
}

function parseQuestionIndex(value: string | undefined): number {
  if (value === undefined) {
    throw new AppError(
      "Question navigation index is missing.",
      ErrorClassification.PERMANENT,
      "MISSING_QUESTION_INDEX",
    );
  }

  const parsed = Number(value);
  if (!Number.isInteger(parsed)) {
    throw new AppError(
      "Question navigation index must be an integer.",
      ErrorClassification.PERMANENT,
      "INVALID_QUESTION_INDEX",
    );
  }

  return parsed;
}

function createRandomSeed(): number {
  const values = new Uint32Array(RANDOM_SEED_ARRAY_LENGTH);
  crypto.getRandomValues(values);
  const seed = values[0];
  if (seed === undefined) {
    throw new AppError(
      "Cryptographic seed generation failed.",
      ErrorClassification.INVARIANT_VIOLATION,
      "MISSING_RANDOM_SEED",
    );
  }

  return seed;
}

const root = document.querySelector<HTMLElement>("#app");
if (root === null) {
  throw new AppError(
    "Application root is missing.",
    ErrorClassification.INVARIANT_VIOLATION,
    "MISSING_APP_ROOT",
  );
}

if (ResourceBudget.MAX_QUESTION_COUNT < ResourceBudget.COMPREHENSIVE_QUESTION_COUNT) {
  throw new AppError(
    "Comprehensive assessment exceeds the configured question budget.",
    ErrorClassification.INVARIANT_VIOLATION,
    "INVALID_QUESTION_BUDGET",
  );
}

new AppController(root, new LocalProgressStore(window.localStorage)).initialize();
